// ADAPTED from ptc-core web/src/hooks/useScreenStateDefault.ts (filter contract v3 §16.1, BINDING
// 2026-11-01): a remembered state is a landing DEFAULT that produces a URL (§6.5). Only the import
// of the screen-state hooks differs (../services/crmViewsApi instead of core's own relative
// ../api/savedViews) -- the logic is unchanged from core's own, already-tested implementation.
//
// - With NO list params in the URL, this person's remembered query for app + screen is written INTO
//   the URL with `replace` (no history entry); from then on the URL is the state. A URL that carries
//   list params is never overridden.
// - Saved on change, debounced. Caller-scoped values ("my clients") are never remembered (§6.5's
//   4-of-1,052 rule), and neither is `n` (a notification's read-on-arrival id, consumed on arrival --
//   this app has no notification bell yet, but the exclusion costs nothing to keep for parity).
// - `reset()` clears the remembered state AND the URL's list params.
//
// The screen says which params are its list state (`isListParam`) and which values are caller-scoped
// (`isCallerScoped`); everything else in the URL (a selected tab, a detail id) is left alone and
// never stored.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDeleteScreenState, usePutScreenState, useScreenState } from '../services/crmViewsApi';

/** Never remembered on any screen: consumed on arrival by a read-on-arrival effect, if the screen
 * ever gains one (this app has none yet -- kept for parity with core's own hook). */
const NEVER_REMEMBERED = new Set(['n']);

export interface ScreenStateDefaultOptions {
  app: string;
  screen: string;
  isListParam: (key: string) => boolean;
  isCallerScoped?: (key: string, value: string) => boolean;
  debounceMs?: number;
  /** False: the hook does nothing at all (no read, no landing, no save), for screens that have not adopted it yet. */
  enabled?: boolean;
  /**
   * A landing target that wins over the remembered state when the URL has no list params: the
   * person's DEFAULT saved view. Landing waits until `ready`; a null `query` falls back to the
   * remembered state.
   */
  prefer?: { ready: boolean; query: string | null };
}

/** The list-state query string of `params`: list params only, minus caller-scoped pairs and `n`, in URL order. */
export function rememberableQuery(
  params: URLSearchParams,
  isListParam: (key: string) => boolean,
  isCallerScoped: (key: string, value: string) => boolean = () => false,
): string {
  const out = new URLSearchParams();
  params.forEach((value, key) => {
    if (NEVER_REMEMBERED.has(key) || !isListParam(key) || isCallerScoped(key, value)) return;
    out.append(key, value);
  });
  return out.toString();
}

export function hasListParams(params: URLSearchParams, isListParam: (key: string) => boolean): boolean {
  for (const key of params.keys()) if (!NEVER_REMEMBERED.has(key) && isListParam(key)) return true;
  return false;
}

/** `current` with its list params replaced by the remembered query; non-list params (a tab, `n`) are kept. */
export function withRemembered(
  current: URLSearchParams,
  remembered: string,
  isListParam: (key: string) => boolean,
): URLSearchParams {
  const next = new URLSearchParams();
  current.forEach((value, key) => {
    if (NEVER_REMEMBERED.has(key) || !isListParam(key)) next.append(key, value);
  });
  new URLSearchParams(remembered).forEach((value, key) => {
    if (isListParam(key) && !NEVER_REMEMBERED.has(key)) next.append(key, value);
  });
  return next;
}

export function useScreenStateDefault({
  app, screen, isListParam, isCallerScoped, debounceMs = 800, enabled = true, prefer,
}: ScreenStateDefaultOptions) {
  const [searchParams, setSearchParams] = useSearchParams();
  const stored = useScreenState(app, screen, enabled);
  const put = usePutScreenState(app, screen);
  const del = useDeleteScreenState(app, screen);

  // Landing: applied at most once, when the remembered state has loaded. `lastSaved` starts as what is stored, so the
  // write effect never echoes the state it just applied back to the server.
  const landed = useRef(false);
  // The same fact as state, so `ready` flips in the render that already carries the landed URL (one list request,
  // for the state the screen lands on).
  const [hasLanded, setHasLanded] = useState(false);
  const lastSaved = useRef<string | null>(null);
  const preferReady = prefer ? prefer.ready : true;
  const preferQuery = prefer?.query ?? null;
  useEffect(() => {
    if (!enabled || landed.current || !(stored.isSuccess || stored.isError) || !preferReady) return;
    landed.current = true;
    const remembered = stored.data?.stateQuery ?? null;
    lastSaved.current = remembered ?? '';
    const target = preferQuery ?? remembered;
    if (target && !hasListParams(searchParams, isListParam)) {
      setSearchParams(withRemembered(searchParams, target, isListParam), { replace: true });
    }
    setHasLanded(true);
  }, [enabled, stored.isSuccess, stored.isError, stored.data, preferReady, preferQuery, searchParams, setSearchParams, isListParam]);

  // Saved on change, debounced. An empty list state forgets the remembered one (nothing left to land on).
  const query = rememberableQuery(searchParams, isListParam, isCallerScoped);
  const { mutate: save } = put;
  const { mutate: forget } = del;
  useEffect(() => {
    if (!enabled || !landed.current || query === lastSaved.current) return;
    const t = setTimeout(() => {
      lastSaved.current = query;
      if (query) save(query);
      else forget();
    }, debounceMs);
    return () => clearTimeout(t);
  }, [enabled, query, debounceMs, save, forget]);

  const reset = useCallback(() => {
    lastSaved.current = '';
    forget();
    const next = new URLSearchParams();
    searchParams.forEach((value, key) => {
      if (NEVER_REMEMBERED.has(key) || !isListParam(key)) next.append(key, value);
    });
    setSearchParams(next, { replace: true });
  }, [forget, searchParams, setSearchParams, isListParam]);

  // Ready once the store has answered (or failed: a dead store must never hold a screen hostage; the URL still works),
  // and the landing has been decided. Disabled = always ready.
  return { ready: !enabled || hasLanded, reset };
}
